/**
 * Exhaust Home — magnetic cards + vehicle quick-finder + scroll reveal + widget gating.
 * Progressive enhancement: cleanly degrades without JS or with reduced motion.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- Page-scoped chrome ---------- */
  // The theme hides the menu drawer from desktop (t4s-d-lg-none). Re-enable it on this page.
  const drawer = document.getElementById('t4s-menu-drawer');
  if (drawer) drawer.classList.remove('t4s-d-lg-none');

  // Guarantee hero background video autoplays smoothly (desktop & mobile)
  const heroVideos = document.querySelectorAll('.exh-hero__video');
  heroVideos.forEach((vid) => {
    vid.muted = true;
    vid.setAttribute('muted', '');
    vid.setAttribute('playsinline', '');
    const tryPlay = () => {
      const p = vid.play();
      if (p !== undefined) p.catch(() => {});
    };
    tryPlay();
    document.addEventListener('touchstart', tryPlay, { once: true, passive: true });
    document.addEventListener('click', tryPlay, { once: true, passive: true });
  });

  /* ---------- Crisp & Floating Widgets Gating (Bidirectional - matches Back-to-Top button) ---------- */
  const crispExec = (action) => {
    try {
      window.$crisp = window.$crisp || [];
      window.$crisp.push(['do', action]);
    } catch (e) {}
  };

  let isChatVisible = false;
  const SCROLL_THRESHOLD = 120;

  const updateCrispVisibility = () => {
    const backToTop = document.getElementById('t4s-backToTop');
    // Align directly with back-to-top visibility or scroll threshold
    const shouldShow = backToTop 
      ? backToTop.classList.contains('is--show') || window.scrollY > SCROLL_THRESHOLD
      : window.scrollY > SCROLL_THRESHOLD;

    if (shouldShow && !isChatVisible) {
      isChatVisible = true;
      root.classList.add('exh-scrolled');
      crispExec('chat:show');
    } else if (!shouldShow && isChatVisible) {
      isChatVisible = false;
      root.classList.remove('exh-scrolled');
      crispExec('chat:hide');
    }
  };

  // Initial state: hide chat immediately
  crispExec('chat:hide');
  root.classList.remove('exh-scrolled');

  let scrollTicking = false;
  window.addEventListener('scroll', () => {
    if (!scrollTicking) {
      window.requestAnimationFrame(() => {
        updateCrispVisibility();
        scrollTicking = false;
      });
      scrollTicking = true;
    }
  }, { passive: true });

  window.addEventListener('load', () => {
    updateCrispVisibility();
    if (window.scrollY <= SCROLL_THRESHOLD) {
      crispExec('chat:hide');
    }
  });

  // Watch for back-to-top class changes if present
  const backToTopBtn = document.getElementById('t4s-backToTop');
  if (backToTopBtn && window.MutationObserver) {
    const bttObserver = new MutationObserver(() => updateCrispVisibility());
    bttObserver.observe(backToTopBtn, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- Vehicle Dropdown Selector ---------- */
  const vehicleData = {
    lamborghini: {
      name: "Lamborghini",
      url: "/collections/lamborghini-yonristic-performance",
      models: [
        { name: "All Lamborghini Exhausts", url: "/collections/lamborghini-yonristic-performance" },
        { name: "Revuelto (Hybrid V12)", url: "/collections/lamborghini-revuelto" },
        { name: "Huracán (LP580 / LP610 / EVO / STO)", url: "/collections/lamborghini-yonristic-performance" },
        { name: "Urus / Urus S / Performante", url: "/collections/lamborghini-yonristic-performance" }
      ]
    },
    ferrari: {
      name: "Ferrari",
      url: "/collections/ferrari-yonristic-performance",
      models: [
        { name: "All Ferrari Exhausts", url: "/collections/ferrari-yonristic-performance" },
        { name: "296 GTB / GTS (Twin-Turbo V6 Hybrid)", url: "/collections/ferrari-yonristic-performance" },
        { name: "488 GTB / Spider / Pista", url: "/collections/ferrari-yonristic-performance" },
        { name: "F8 Tributo / Spider", url: "/collections/ferrari-yonristic-performance" }
      ]
    },
    mclaren: {
      name: "McLaren",
      url: "/collections/mclaren",
      models: [
        { name: "All McLaren Exhausts", url: "/collections/mclaren" },
        { name: "720S / 765LT", url: "/collections/720s" },
        { name: "570S / 570GT", url: "/collections/mclaren-570" },
        { name: "McLaren GT", url: "/collections/mclaren-gt" }
      ]
    },
    bmw: {
      name: "BMW",
      url: "/pages/yonristic-performance-bmw-exhaust-collections",
      models: [
        { name: "All BMW Performance Exhausts", url: "/pages/yonristic-performance-bmw-exhaust-collections" },
        { name: "G80 M3 / G82 G83 M4 (S58)", url: "/collections/bmw-g80-m3-vanta-gold" },
        { name: "G87 M2", url: "/collections/bmw-g87-m2-vanta-gold" },
        { name: "G90 / G99 M5 (V8 Hybrid)", url: "/collections/bmw-m5-vanta-gold" },
        { name: "F97 X3M / F98 X4M", url: "/collections/x3m" },
        { name: "G20 M340i / M440i (B58)", url: "/pages/yonristic-performance-bmw-exhaust-collections" }
      ]
    },
    porsche: {
      name: "Porsche",
      url: "/collections/porsche-yonristic-performance",
      models: [
        { name: "All Porsche Exhausts", url: "/collections/porsche-yonristic-performance" },
        { name: "911 (992.1) GT3 / GT3 RS", url: "/collections/porsche-gt3" },
        { name: "911 (992) Carrera / S / GTS", url: "/collections/porsche-yonristic-performance" },
        { name: "911 (991.1 / 991.2)", url: "/collections/porsche-yonristic-performance" },
        { name: "718 / 981 Cayman / Boxster", url: "/collections/porsche-yonristic-performance" }
      ]
    },
    corvette: {
      name: "Chevrolet Corvette",
      url: "/pages/chevy-corvette-c8-exhaust",
      models: [
        { name: "All Corvette C8 Exhausts", url: "/pages/chevy-corvette-c8-exhaust" },
        { name: "C8 Stingray (LT2)", url: "/pages/chevy-corvette-c8-exhaust" },
        { name: "C8 Z06 (LT6 Flat-Plane Crank)", url: "/pages/chevy-corvette-c8-exhaust" },
        { name: "C8 E-Ray (AWD Hybrid)", url: "/pages/chevy-corvette-c8-exhaust" }
      ]
    },
    mercedes: {
      name: "Mercedes-Benz",
      url: "/collections/mercedes-benz-yonristic-performance",
      models: [
        { name: "All Mercedes-Benz Exhausts", url: "/collections/mercedes-benz-yonristic-performance" },
        { name: "AMG GT / GT-R / GT-S (C190)", url: "/collections/mercedes-benz-yonristic-performance" },
        { name: "C63 / C63S AMG (W205)", url: "/collections/mercedes-benz-yonristic-performance" },
        { name: "G63 AMG (W463A)", url: "/collections/mercedes-benz-yonristic-performance" }
      ]
    },
    audi: {
      name: "Audi",
      url: "/collections/audi-yonristic-performance",
      models: [
        { name: "All Audi Performance Exhausts", url: "/collections/audi-yonristic-performance" },
        { name: "R8 V10 (Gen 1 & Gen 2)", url: "/collections/audi-yonristic-performance" },
        { name: "RS6 / RS7 Avant (C8)", url: "/collections/audi-yonristic-performance" },
        { name: "RSQ8", url: "/collections/audi-yonristic-performance" }
      ]
    }
  };

  const brandSelect = document.getElementById('exh-select-brand');
  const modelSelect = document.getElementById('exh-select-model');
  const submitBtn = document.getElementById('exh-finder-submit');

  if (brandSelect && modelSelect && submitBtn) {
    brandSelect.addEventListener('change', () => {
      const selectedBrandKey = brandSelect.value;
      const brandInfo = vehicleData[selectedBrandKey];

      modelSelect.innerHTML = '';
      if (brandInfo && brandInfo.models) {
        brandInfo.models.forEach((m, idx) => {
          const opt = document.createElement('option');
          opt.value = m.url;
          opt.textContent = m.name;
          if (idx === 0) opt.selected = true;
          modelSelect.appendChild(opt);
        });
        modelSelect.disabled = false;
        submitBtn.disabled = false;
      } else {
        modelSelect.disabled = true;
        submitBtn.disabled = true;
      }
    });

    submitBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const targetUrl = modelSelect.value || (vehicleData[brandSelect.value] && vehicleData[brandSelect.value].url);
      if (targetUrl) {
        window.location.href = targetUrl;
      }
    });
  }

  /* ---------- Scroll reveal ---------- */
  const revealItems = document.querySelectorAll('.exh [data-reveal]');
  if (!reduceMotion && 'IntersectionObserver' in window) {
    root.classList.add('exh-js');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' }
    );
    revealItems.forEach((el) => io.observe(el));

    const checkRevealInView = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      revealItems.forEach((el) => {
        if (el.classList.contains('is-in')) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < vh * 0.94 && rect.bottom > 0) {
          el.classList.add('is-in');
          io.unobserve(el);
        }
      });
    };
    window.addEventListener('scroll', checkRevealInView, { passive: true });
    window.addEventListener('resize', checkRevealInView, { passive: true });
    setTimeout(checkRevealInView, 400);
    setTimeout(checkRevealInView, 1600);
  }

  /* ---------- Magnetic cards ---------- */
  if (!reduceMotion && canHover) {
    const PULL = 14;
    const TILT = 7;

    document.querySelectorAll('.exh [data-magnetic]').forEach((card) => {
      let frame = 0;
      let rect = null;

      const apply = (e) => {
        frame = 0;
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        const dx = x - 0.5;
        const dy = y - 0.5;
        card.style.setProperty('--tx', `${(dx * PULL * 2).toFixed(2)}px`);
        card.style.setProperty('--ty', `${(dy * PULL * 2).toFixed(2)}px`);
        card.style.setProperty('--rx', `${(-dy * TILT * 2).toFixed(2)}deg`);
        card.style.setProperty('--ry', `${(dx * TILT * 2).toFixed(2)}deg`);
        card.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`);
        card.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`);
      };

      card.addEventListener('pointerenter', () => {
        rect = card.getBoundingClientRect();
        card.classList.add('is-active');
      });

      card.addEventListener('pointermove', (e) => {
        if (!rect) rect = card.getBoundingClientRect();
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => apply(e));
      });

      card.addEventListener('pointerleave', () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        rect = null;
        card.classList.remove('is-active');
        ['--tx', '--ty', '--rx', '--ry'].forEach((p) => card.style.setProperty(p, '0px'));
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
      });
    });
  }

  /* ---------- Modern FAQ Accordion (robust delegation) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.exh-faq__btn');
    if (!btn) return;
    const item = btn.closest('.exh-faq__item');
    if (!item) return;

    const list = item.closest('.exh-faq__list');
    const wasOpen = item.classList.contains('is-open');

    // Accordion: close others in same list
    if (list) {
      list.querySelectorAll('.exh-faq__item.is-open').forEach((other) => {
        if (other !== item) {
          other.classList.remove('is-open');
          const otherBtn = other.querySelector('.exh-faq__btn');
          const otherPanel = other.querySelector('.exh-faq__panel');
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
          if (otherPanel) otherPanel.hidden = true;
        }
      });
    }

    if (wasOpen) {
      item.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      const panel = item.querySelector('.exh-faq__panel');
      if (panel) panel.hidden = true;
    } else {
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      const panel = item.querySelector('.exh-faq__panel');
      if (panel) panel.hidden = false;
    }
  });

  /* ---------- Collapsible Custom Inquiry Panel ---------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-inquiry-toggle]');
    if (!toggle) return;
    const panel = toggle.closest('[data-inquiry-panel]');
    if (!panel) return;

    const isOpen = panel.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    const body = panel.querySelector('.exh-inquiry__collapse');
    if (body) body.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    const btnTxt = panel.querySelector('.exh-inquiry__btn-txt');
    if (btnTxt) {
      btnTxt.textContent = isOpen
        ? (btnTxt.getAttribute('data-text-open') || 'Close Form')
        : (btnTxt.getAttribute('data-text-closed') || 'Custom Inquiry');
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const toggle = e.target.closest('[data-inquiry-toggle]');
    if (!toggle) return;
    e.preventDefault();
    toggle.click();
  });

  /* ---------- Hulk Form Builder Rounded UI & Font Bridge ---------- */
  const HULK_FORM_ID = 'qmWV5bn2TIF3s5zwtqZ18g';
  const FONT_INJECTION = "#ffffff !important;} </style><link rel=\"stylesheet\" href=\"https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Space+Grotesk:wght@400;500;600&display=swap\"><style> * { font-family: 'Space Grotesk', 'Rajdhani', sans-serif !important; } h1, h2, .form_title, .form-title, .form_description, .form-description, .form_sub_title { display: none !important; } h3, .block_label, label, .question_label, button, .btn, .form_submit_div button { font-family: 'Rajdhani', 'Space Grotesk', sans-serif !important; letter-spacing: 0.05em !important; } .form_submit_div button { text-transform: uppercase !important; font-weight: 700 !important; border-radius: 10px !important; } input, select, textarea, .form-control { font-family: 'Space Grotesk', sans-serif !important; border-radius: 10px !important; } .form_generater_form_div { border-radius: 16px !important; padding-top: 8px !important; } .dummy {";

  const hulkThemeSettings = [{
    form_uuid: HULK_FORM_ID,
    form_settings: "true",
    fill_color: "#ffffff",
    padding: "0",
    border_color: "#ffffff",
    border_radius: "16",
    border: "0",
    form_width: "800",
    fonts: "'Rajdhani', 'Space Grotesk', sans-serif",
    font_color: "#121316",
    size: "15",
    btn_text: "Submit Inquiry",
    sub_btn_bg_color: "#121316",
    sub_btn_txt_color: "#ffffff",
    sub_btn_font_size: "15",
    sub_btn_bdr_color: "#121316",
    sub_btn_bdr_rad: "10",
    sub_btn_bdr_width: "1",
    input_font_color: "#121316",
    input_size: "14",
    input_background_color: "#f4f5f7",
    input_border_radius: "10",
    input_border_color: "#dce0e5",
    input_background_color_focus: FONT_INJECTION,
    input_border_color_focus: "#121316"
  }];

  window.FbThemeAppExtSettingsHash = window.FbThemeAppExtSettingsHash || {};
  if (Array.isArray(window.FbThemeAppExtSettingsHash)) {
    window.FbThemeAppExtSettingsHash = hulkThemeSettings;
  } else {
    window.FbThemeAppExtSettingsHash[HULK_FORM_ID] = hulkThemeSettings[0];
  }

  // Pre-seed in-memory form metadata if present
  try {
    if (window.hulkFormBuilder && window.hulkFormBuilder.form_data && window.hulkFormBuilder.form_data["form_" + HULK_FORM_ID]) {
      const fentry = window.hulkFormBuilder.form_data["form_" + HULK_FORM_ID];
      if (fentry && fentry.form_data) {
        fentry.form_data.form_title = "";
        fentry.form_data.form_description = "";
        fentry.form_data.label_font_family = "Rajdhani:500,600,700";
        fentry.form_data.input_font_family = "Space+Grotesk:400,500,600";
        fentry.form_data.input_border_radius = "10";
        fentry.form_data.button_border_radius = "10";
        fentry.form_data.form_border_radius = "16";
      }
    }
  } catch (e) {}

  function sendHulkThemePostMessage(iframe) {
    if (!iframe || !iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(JSON.stringify(hulkThemeSettings), '*');
      iframe.contentWindow.postMessage(JSON.stringify(window.FbThemeAppExtSettingsHash), '*');
    } catch (e) {}

    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      if (doc && doc.head && !doc.getElementById('exh-inquiry-injected-rounded')) {
        const link = doc.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Space+Grotesk:wght@400;500;600&display=swap';
        doc.head.appendChild(link);

        const style = doc.createElement('style');
        style.id = 'exh-inquiry-injected-rounded';
        style.textContent = `
          * {
            font-family: 'Space Grotesk', 'Rajdhani', sans-serif !important;
          }
          h1, h2, h3, .form_title, .block_label, label, .question_label, button, .btn, .form_submit_div button {
            font-family: 'Rajdhani', 'Space Grotesk', sans-serif !important;
            letter-spacing: 0.05em !important;
          }
          .form_submit_div button {
            text-transform: uppercase !important;
            font-weight: 700 !important;
            border-radius: 10px !important;
          }
          input, select, textarea, .form-control {
            font-family: 'Space Grotesk', sans-serif !important;
            border-radius: 10px !important;
            border: 1px solid #dce0e5 !important;
            background: #f4f5f7 !important;
          }
          input:focus, select:focus, textarea:focus {
            border-color: #121316 !important;
            background: #ffffff !important;
          }
          button, .btn, input[type="submit"], div.form_submit_div button {
            border-radius: 10px !important;
          }
          .form_generater_form_div {
            border-radius: 16px !important;
          }
        `;
        doc.head.appendChild(style);
      }
    } catch (e) {}
  }

  function initHulkFormRounding() {
    const inquiryWrap = document.querySelector('.exh-inquiry');
    if (!inquiryWrap) return;

    const syncIframes = () => {
      const iframes = inquiryWrap.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        sendHulkThemePostMessage(iframe);
        if (!iframe.dataset.exhStyled) {
          iframe.dataset.exhStyled = 'true';
          iframe.addEventListener('load', () => {
            sendHulkThemePostMessage(iframe);
            setTimeout(() => sendHulkThemePostMessage(iframe), 300);
            setTimeout(() => sendHulkThemePostMessage(iframe), 1200);
          });
        }
      });
    };

    syncIframes();

    if ('MutationObserver' in window) {
      const mo = new MutationObserver(() => syncIframes());
      mo.observe(inquiryWrap, { childList: true, subtree: true });
    }

    window.addEventListener('message', (event) => {
      if (event.data && typeof event.data === 'object' && event.data.formid === HULK_FORM_ID) {
        syncIframes();
      }
    });

    [250, 600, 1200, 2500, 4500].forEach((delay) => {
      setTimeout(syncIframes, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHulkFormRounding);
  } else {
    initHulkFormRounding();
  }
})();
